import { type Filters, useInResiStore } from "@superbright/indexeddb-orm";
import { useQuery } from "@tanstack/react-query";

import { fetchBootstrap, type BootstrapData } from "api/bootstrap";
import { queryKeys } from "api/config";
import { isFiltersAtDefaults } from "features/filters/dynamicFilters";
import { filterActions } from "store/actions";
import { queryClient } from "utils";
import { VisitorNotFoundException } from "utils/http";
import { hasFilterParams } from "utils/urlFilters";

/**
 * Apply bootstrap side-effects (filter sync).
 * Shared between the normal fetch path and the SSR cache-hit path.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyBootstrapSideEffects(data: any) {
  if (data.current_filter?.filter) {
    // If the URL already carries filter params, they take precedence
    // over server-saved filters (shareable links must win).
    const urlHasFilters =
      typeof window !== "undefined" &&
      hasFilterParams(new URLSearchParams(window.location.search));
    const { filters } = useInResiStore.getState();
    if (!urlHasFilters && isFiltersAtDefaults(filters)) {
      filterActions.set(data.current_filter.filter as Partial<Filters>);
    }
  }
}

export const useBootstrap = (
  slug: string | undefined,
  visitorId: string | undefined
) => {
  return useQuery({
    queryKey: queryKeys.bootstrap(slug!, visitorId!),
    queryFn: async () => {
      // If SSR already seeded bootstrap data, return it from cache
      // instead of making a duplicate network call.
      const key = queryKeys.bootstrap(slug!, visitorId!);
      const cached = queryClient.getQueryData<BootstrapData>(key);
      if (cached) {
        // eslint-disable-next-line no-console
        applyBootstrapSideEffects(cached);
        return cached;
      }

      const data = await fetchBootstrap(slug!, visitorId!);

      applyBootstrapSideEffects(data);
      return data;
    },
    enabled: !!slug && !!visitorId,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: (failureCount, error) => {
      if (error instanceof VisitorNotFoundException) return false;
      return failureCount < 3;
    },
  });
};
